import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import ErrorBoundary from './ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import app from './firebase';
import { AuthProvider } from './components/Auth/AuthProvider';

// 🔕 Desactivar cualquier diálogo del navegador tipo "¿Seguro que quieres salir?" / "Es posible que los cambios no se guarden"
// Bloquea el registro de listeners 'beforeunload' y limpia cualquier handler existente
if (typeof window !== 'undefined') {
  try {
    window.onbeforeunload = null;
    const originalAddEventListener = window.addEventListener.bind(window);
    window.addEventListener = (type, listener, options) => {
      if (type === 'beforeunload') {
        return; // Ignorar cualquier intento de registrar beforeunload
      }
      return originalAddEventListener(type, listener, options);
    };
  } catch (_) {
    // noop
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  </React.StrictMode>
);

serviceWorkerRegistration.register();

// Flag para limpiar Service Worker y cachés: agrega ?clearSW=1 a la URL
if (window.location.search.includes('clearSW=1') && 'serviceWorker' in navigator) {
  (async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
      if (window.caches && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch (_) {}
    window.location.replace(window.location.origin);
  })();
}

// Logger global de errores a Cloud Functions (carga diferida para no bloquear el arranque)
let logClientErrorPromise = null;
function getLogClientError() {
  if (!logClientErrorPromise) {
    logClientErrorPromise = import('firebase/functions').then(({ getFunctions, httpsCallable }) => {
      const functions = getFunctions(app, process.env.REACT_APP_FIREBASE_REGION || 'us-central1');
      return httpsCallable(functions, 'logClientError');
    }).catch(() => () => {});
  }
  return logClientErrorPromise;
}
function queueErrorLog(payload) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  getLogClientError()
    .then((logClientError) => {
      const p = logClientError(payload);
      if (p && typeof p.catch === 'function') p.catch(() => {});
    })
    .catch(() => {});
}
window.addEventListener('error', (event) => {
  try {
    queueErrorLog({
      message: event?.error?.message || event?.message || 'Unknown error',
      stack: event?.error?.stack || null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      extra: { type: 'error' }
    });
  } catch (_) {}
});
window.addEventListener('unhandledrejection', (event) => {
  try {
    const reason = event?.reason;
    queueErrorLog({
      message: (reason && reason.message) ? reason.message : String(reason || 'Unhandled rejection'),
      stack: (reason && reason.stack) ? reason.stack : null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      extra: { type: 'unhandledrejection' }
    });
  } catch (_) {}
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.ready.then(registration => {
      if (registration && registration.active) {
        navigator.serviceWorker.addEventListener('message', event => {
          if (event.data && event.data.type === 'NEW_VERSION_AVAILABLE') {
            if (window.confirm('Hay una nueva versión de Ratafit disponible. ¿Actualizar ahora?')) {
              // Forzar recarga sin caché: el SW devuelve "/" desde caché y no actualiza.
              // Añadiendo un query param la URL no coincide con la entrada cacheada y se pide de red.
              const url = new URL(window.location.href);
              url.searchParams.set('_refresh', Date.now().toString());
              window.location.replace(url.toString());
            }
          }
        });
      }
    });
  });
}
